import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, ExternalLink, Send, CheckCircle2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { api } from "../lib/api";

const BASE = process.env.REACT_APP_BACKEND_URL;
const dkey = (a) => `ann_${a.id}_v${a.version || 1}`;
const dismissed = (a) => localStorage.getItem(dkey(a)) === "1" || (a.id === "petroapp" && (a.version || 1) === 1 && localStorage.getItem("petroapp_announce_v1") === "1");
export const annImg = (u) => (u && u.startsWith("/api/") ? `${BASE}${u}` : u);

const RequestDialog = ({ a, open, onOpenChange, onDone }) => {
  const [f, setF] = useState({ vehicle_count: "", phone: "", note: "" }), [busy, setBusy] = useState(false);
  useEffect(() => { if (open) api.announcementVehicleCount().then((r) => setF((p) => ({ ...p, vehicle_count: r.vehicle_count }))).catch(() => {}); }, [open]);
  const send = async () => {
    setBusy(true);
    try { await api.requestAnnouncement(a.id, { ...f, vehicle_count: Number(f.vehicle_count) || 0 }); toast.success("تم إرسال طلبك، وسنتواصل معك قريباً"); onDone(); onOpenChange(false); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر إرسال الطلب"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className="w-[95vw] max-w-sm" data-testid="announcement-request-dialog">
        <DialogHeader className="text-start"><DialogTitle>{a.request_label}</DialogTitle><DialogDescription className="text-start">{a.title}</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><label className="mb-1 block text-xs font-semibold text-slate-600">عدد المركبات</label>
            <Input type="number" min={0} value={f.vehicle_count} onChange={(e) => setF({ ...f, vehicle_count: e.target.value })} data-testid="ann-req-vehicles" />
            <p className="mt-1 text-[11px] text-slate-400">محسوب تلقائياً من مركبات شركتك — عدّله إذا لزم.</p></div>
          <div><label className="mb-1 block text-xs font-semibold text-slate-600">جوال التواصل</label>
            <Input dir="ltr" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="05xxxxxxxx" data-testid="ann-req-phone" /></div>
          <div><label className="mb-1 block text-xs font-semibold text-slate-600">ملاحظة (اختياري)</label>
            <Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="ann-req-note" /></div>
          <Button className="w-full gap-2" disabled={busy} onClick={send} data-testid="ann-req-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} إرسال الطلب</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const AnnouncementCard = ({ a, onClose, onRequested, popup }) => {
  const [req, setReq] = useState(false);
  return (
    <div dir="rtl" data-testid={`announcement-${a.id}`}
      className={`relative flex items-start gap-4 overflow-hidden ${popup ? "flex-col items-center text-center" : "rounded-2xl border border-blue-200 bg-gradient-to-l from-blue-50 to-white p-4 pe-10 shadow-sm animate-in fade-in slide-in-from-top-2 duration-500"}`}>
      {a.image_url && <img src={annImg(a.image_url)} alt="" className={`shrink-0 rounded-xl object-cover shadow ${popup ? "h-24 w-24" : "h-16 w-16 sm:h-20 sm:w-20"}`} />}
      <div className="min-w-0 space-y-1.5 text-sm leading-relaxed text-slate-700">
        <p className={`font-bold text-blue-700 ${popup ? "text-xl" : "text-base"}`}>{a.title}</p>
        {a.body && <p className="whitespace-pre-line">{a.body}</p>}
        {a.highlight && <p className="inline-block rounded-lg bg-blue-600 px-2.5 py-1 text-xs font-bold text-white">{a.highlight}</p>}
        {a.footer && <p className="text-xs text-slate-500">{a.footer}</p>}
        <div className={`flex flex-wrap gap-2 pt-1 ${popup ? "justify-center" : ""}`}>
          {a.request_label && (a.requested
            ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-3 py-1.5 text-xs font-semibold text-emerald-700" data-testid={`announcement-requested-${a.id}`}><CheckCircle2 className="h-3.5 w-3.5" /> تم إرسال طلبك</span>
            : <button onClick={() => setReq(true)} data-testid={`announcement-request-${a.id}`} className="inline-flex items-center gap-1 rounded-full bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-blue-700"><Send className="h-3.5 w-3.5" /> {a.request_label}</button>)}
          {a.cta_label && a.cta_url && (
            <a href={a.cta_url} target="_blank" rel="noreferrer" data-testid={`announcement-cta-${a.id}`}
              className="inline-flex items-center gap-1 rounded-full bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-slate-700">
              {a.cta_label} <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
        </div>
      </div>
      {onClose && !popup && (
        <button onClick={onClose} aria-label="إغلاق" data-testid={`announcement-close-${a.id}`}
          className="absolute end-2 top-2 rounded-full p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700">
          <X className="h-4 w-4" />
        </button>
      )}
      {a.id !== "preview" && <RequestDialog a={a} open={req} onOpenChange={setReq} onDone={onRequested || (() => {})} />}
    </div>
  );
};

export const AnnouncementsBar = ({ page }) => {
  const [list, setList] = useState([]);
  const load = () => api.activeAnnouncements(page).then(setList).catch(() => setList([]));
  useEffect(() => {
    load(); window.addEventListener("announcements:changed", load);
    return () => window.removeEventListener("announcements:changed", load);
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps
  const shown = list.filter((a) => !dismissed(a));
  const close = (a) => { localStorage.setItem(dkey(a), "1"); setList((l) => [...l]); };
  const banners = shown.filter((a) => a.display !== "popup");
  const pop = shown.find((a) => a.display === "popup");
  if (!shown.length) return null;
  return (
    <>
      {banners.length > 0 && <div className="space-y-3" data-testid={`announcements-${page}`}>{banners.map((a) => <AnnouncementCard key={a.id} a={a} onClose={() => close(a)} onRequested={load} />)}</div>}
      {pop && (
        <Dialog open onOpenChange={(o) => !o && close(pop)}>
          <DialogContent dir="rtl" className="w-[92vw] max-w-md" data-testid={`announcement-popup-${pop.id}`}>
            <DialogHeader className="sr-only"><DialogTitle>{pop.title}</DialogTitle><DialogDescription>{pop.body}</DialogDescription></DialogHeader>
            <AnnouncementCard a={pop} popup onRequested={load} />
            <Button variant="outline" onClick={() => close(pop)} data-testid={`announcement-popup-close-${pop.id}`}>تم، إغلاق</Button>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};
